import { useEffect } from 'react'
import { useGetCompanyProfileQuery } from '@/features/settings/settings-api'

/**
 * Reads the tenant's company profile and writes brand colours to CSS
 * variables on `<html>`. Pages that use the `bg-brand`, `text-brand`
 * etc. utility classes pick up the per-tenant colour automatically.
 *
 * Mounted once near the app root.
 */
export function BrandingSync() {
  const { data } = useGetCompanyProfileQuery(undefined, {
    // No need to repoll; settings change rarely
    refetchOnFocus: false,
    refetchOnReconnect: false,
  })

  useEffect(() => {
    const primary = data?.data?.brand_primary_color
    if (!primary) return
    const hsl = hexToHsl(primary)
    if (!hsl) return
    // The base stylesheet defines --accent etc. as HSL triplets, so
    // we override with the same format. Apps using Tailwind tokens
    // like `text-accent` will pick this up.
    document.documentElement.style.setProperty('--accent', hsl)
    document.documentElement.style.setProperty('--brand', hsl)
  }, [data])

  return null
}

/** Convert "#0E1B2C" or "#fff" → "h s% l%" suitable for Tailwind/CSS HSL vars. */
export function hexToHsl(hex: string): string | null {
  let h = hex.replace('#', '').trim()
  if (h.length === 3) {
    h = h.split('').map((c) => c + c).join('')
  }
  if (h.length !== 6 || !/^[0-9a-fA-F]+$/.test(h)) return null
  const r = parseInt(h.slice(0, 2), 16) / 255
  const g = parseInt(h.slice(2, 4), 16) / 255
  const b = parseInt(h.slice(4, 6), 16) / 255
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const l = (max + min) / 2
  let hue = 0
  let sat = 0
  if (max !== min) {
    const d = max - min
    sat = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case r: hue = (g - b) / d + (g < b ? 6 : 0); break
      case g: hue = (b - r) / d + 2; break
      case b: hue = (r - g) / d + 4; break
    }
    hue /= 6
  }
  return `${Math.round(hue * 360)} ${Math.round(sat * 100)}% ${Math.round(l * 100)}%`
}
